import React from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmModal from '../confirm-modal'
import { AllianceRequestProfile, AllianceRequestMoreNote } from './allianceRequestModalShared'
import type { AllianceJoinRequestItem } from '../../common/allianceApi'

interface AllianceJoinRequestModalProps {
  open: boolean
  allianceName?: string
  requests: AllianceJoinRequestItem[]
  /** Request id currently being approved/declined (buttons disabled while set). */
  busyId?: string | null
  onApprove: (requestId: string) => void
  onDecline: (requestId: string) => void
  onClose: () => void
}

/**
 * Leader-side join-request prompt, rendered with the game's shared ConfirmModal
 * (parchment sheet, gold Approve / red Decline plates). Requests are shown ONE AT
 * A TIME: Approve adds the member, Decline dismisses; the watcher then surfaces
 * the next pending one. Closing (X / backdrop) only dismisses the prompt.
 */
export const AllianceJoinRequestModal: React.FC<AllianceJoinRequestModalProps> = ({
  open,
  allianceName,
  requests,
  busyId,
  onApprove,
  onDecline,
  onClose,
}) => {
  const { t } = useTranslation()
  const req = requests[0]
  if (!open || !req) return null

  const busy = busyId === req._id

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={() => onApprove(req._id)}
      onCancel={() => onDecline(req._id)}
      title={t('alliance.joinRequestTitle', { defaultValue: 'Join Request' })}
      confirmText={t('alliance.approve', { defaultValue: 'Approve' })}
      cancelText={t('alliance.decline', { defaultValue: 'Decline' })}
      isProcessing={busy}
      content={
        <div className="flex w-full flex-col items-center gap-3 text-center">
          <AllianceRequestProfile req={req} />
          <p className="confirm-modal__message">
            {t('alliance.joinRequestWants', {
              defaultValue: 'wants to join {{name}}. Approve to add them.',
              name: allianceName || t('alliance.alliance', { defaultValue: 'your alliance' }),
            })}
            {requests.length > 1 ? (
              <>
                <br />
                <AllianceRequestMoreNote remaining={requests.length} />
              </>
            ) : null}
          </p>
        </div>
      }
    />
  )
}

export default AllianceJoinRequestModal
